import { useEffect, useState } from 'react'
import { Button, Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '~/shared/ui'
import type { AmountType, CodeGroupFormValues } from '../../../../model'

import { CarcRarcCollapsibleGroup } from './carc-rarc-collapsible-group'
import type { DeductionField, GroupState, UnifiedGroup } from './carc-rarc-group-types'
import {
  EMPTY_GROUP,
  codeGroupToGroupState,
  groupStateToCodeGroup,
  isGroupValid,
} from './carc-rarc-group-types'

interface CarcRarcTaggingSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialCodeGroups: CodeGroupFormValues[]
  columnLabel: string
  deductionField: DeductionField
  amountType: AmountType
  /**
   * Called on save. `summedAmount` is the sum of per-code amounts, or null when every
   * code group has a null amount (caller should preserve existing cell value in that case).
   */
  onSave: (codeGroups: CodeGroupFormValues[], summedAmount: number | null) => void
}

/**
 * Side sheet for selecting CARC + RARC codes and payor-specific code
 * on a service line deduction field. Supports multiple CARC/RARC groups.
 */
export function CarcRarcTaggingSheet({
  open,
  onOpenChange,
  initialCodeGroups,
  columnLabel,
  deductionField,
  amountType,
  onSave,
}: CarcRarcTaggingSheetProps) {
  const [groups, setGroups] = useState<UnifiedGroup[]>([])

  // Reset local state from props when sheet opens
  useEffect(() => {
    if (open) {
      if (initialCodeGroups.length > 0) {
        // Existing codes → collapsed groups, no empty form
        setGroups(
          initialCodeGroups.map((cg) => ({
            key: cg.key,
            state: codeGroupToGroupState(cg),
            collapsed: true,
          })),
        )
      } else {
        // No codes → one empty expanded group
        setGroups([{ key: null, state: { ...EMPTY_GROUP }, collapsed: false }])
      }
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleGroupStateChange(index: number, state: GroupState) {
    setGroups((prev) => prev.map((g, i) => (i === index ? { ...g, state } : g)))
  }

  function handleDeleteGroup(index: number) {
    setGroups((prev) => prev.filter((_, i) => i !== index))
  }

  function handleToggleCollapse(index: number) {
    setGroups((prev) => prev.map((g, i) => (i === index ? { ...g, collapsed: !g.collapsed } : g)))
  }

  function handleAddGroup() {
    setGroups((prev) => [
      // Collapse all valid expanded groups
      ...prev.map((g) => (isGroupValid(g.state) && !g.collapsed ? { ...g, collapsed: true } : g)),
      // Append new empty expanded group
      { key: null, state: { ...EMPTY_GROUP }, collapsed: false },
    ])
  }

  const allValid = groups.length === 0 || groups.every((g) => isGroupValid(g.state))
  const canAddGroup = !groups.some((g) => !g.collapsed && !isGroupValid(g.state))

  function handleSave() {
    const allGroups: CodeGroupFormValues[] = groups
      .filter((g) => isGroupValid(g.state))
      .map((g) => groupStateToCodeGroup(g.state, g.key))
    const amounts = allGroups.map((g) => g.amount).filter((a): a is number => a !== null)
    const summedAmount = amounts.length > 0 ? amounts.reduce((s, a) => s + a, 0) : null
    onSave(allGroups, summedAmount)
    onOpenChange(false)
  }

  function handleCancel() {
    onOpenChange(false)
  }

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      modal={false}
    >
      <SheetContent className="w-[430px]">
        <SheetHeader>
          <SheetTitle>Tagging &ndash; {columnLabel}</SheetTitle>
        </SheetHeader>

        <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
          {groups.map((group, index) => (
            <CarcRarcCollapsibleGroup
              key={group.key ?? index}
              index={index}
              group={group}
              deductionField={deductionField}
              amountType={amountType}
              onStateChange={(state) => handleGroupStateChange(index, state)}
              onDelete={() => handleDeleteGroup(index)}
              onToggleCollapse={() => handleToggleCollapse(index)}
            />
          ))}

          {/* Add another group — disabled until current editing group has a CARC */}
          <Button
            className="mt-2"
            size="small"
            materialIcon="add"
            disabled={!canAddGroup}
            onClick={handleAddGroup}
          >
            CARC &lt;&gt; RARC
          </Button>
        </div>

        <SheetFooter className="flex items-center justify-end gap-4">
          <Button
            size="small"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            raised
            size="small"
            disabled={!allValid}
            onClick={handleSave}
          >
            Save CARC / RARC
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
